// pages/ProjectManagerDashboard.js
// Dashboard Project Manager V2 + Follow-up commerciale V3.
// OGGI (4 KPI follow-up: diretta + team supervisionato) · OPPORTUNITÀ DA SEGUIRE (+ Agenda) · PORTAFOGLIO COMMESSE.
// Read-model: GET /api/dashboard/pm. Motore follow-up condiviso con la Dashboard Tecnico.

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import { DashboardKpiCard } from '../components/dashboard/DashboardKpiCard';
import { FollowupOpportunities } from '../components/dashboard/FollowupOpportunities';
import { CommesseBlock } from '../components/dashboard/CommesseBlock';
import { ProssimeAttivita } from '../components/dashboard/ProssimeAttivita';

const ProjectManagerDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [oppFilter, setOppFilter] = useState('priority');
  const [oppExpanded, setOppExpanded] = useState(false);
  const [attExpanded, setAttExpanded] = useState(false);
  const oppRef = useRef(null);
  const agendaRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await dashboardApi.getPm();
        setData(res.data);
      } catch (e) {
        console.error('Errore dashboard PM:', e);
        toast.error('Errore nel caricamento della dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Errore nel caricamento della dashboard</p>
      </div>
    );
  }

  const {
    title, subtitle,
    followup_kpis = [], followup_opportunities = [],
    supervised_technicians = [], da_assegnare_leads = [],
    project_kpis = [], my_projects = [], project_technicians = [],
    upcoming_appointments = [], prossime_attivita = [], prossime_attivita_summary = {},
  } = data;

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const kpiAction = (kpi) => {
    const map = { nuovi_assegnati: 'nuovi', da_ricontattare: 'da_ricontattare', da_seguire: 'da_ricontattare', in_preventivazione: 'preventivazione', in_trattativa: 'trattativa' };
    setOppFilter(map[kpi.id] || 'priority');
    scrollTo(oppRef);
  };
  const warnAction = (kpi) => {
    setOppFilter(kpi.id === 'in_preventivazione' ? 'preventivazione_warn' : 'trattativa_warn');
    scrollTo(oppRef);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pm-dashboard">
      {/* HEADER */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#3C3C3C]">
          {title || `Benvenuto, ${user?.first_name}`}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
      </div>

      {/* ROW 1 · OGGI (4 KPI follow-up) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pm-kpis">
        {followup_kpis.map((kpi) => (
          <DashboardKpiCard key={kpi.id} kpi={kpi} testIdPrefix="pm-kpi"
            onAction={() => kpiAction(kpi)} onWarningAction={() => warnAction(kpi)} />
        ))}
      </div>

      {/* ROW 2 · OPPORTUNITÀ DA SEGUIRE (65%) + AGENDA (35%) */}
      <div className={`grid grid-cols-1 xl:grid-cols-3 gap-6 ${oppExpanded || attExpanded ? 'xl:items-start' : 'xl:items-stretch'}`}>
        <div className={`xl:col-span-2 ${oppExpanded || attExpanded ? '' : 'h-full'}`}>
          <FollowupOpportunities
            title="Opportunità da seguire"
            items={followup_opportunities}
            daAssegnare={da_assegnare_leads}
            technicians={supervised_technicians}
            pmMode
            filter={oppFilter}
            setFilter={setOppFilter}
            listRef={oppRef}
            onExpandChange={setOppExpanded}
          />
        </div>
        <div className={`xl:col-span-1 ${oppExpanded || attExpanded ? '' : 'h-full'}`} ref={agendaRef}>
          <ProssimeAttivita activities={prossime_attivita} summary={prossime_attivita_summary} initialLimit={3} onExpandChange={setAttExpanded} />
        </div>
      </div>

      {/* ROW 3 · PORTAFOGLIO COMMESSE (invariato) */}
      <CommesseBlock summary={project_kpis} projects={my_projects} technicians={project_technicians} />
    </div>
  );
};

export default ProjectManagerDashboard;
